import React, { useState } from 'react';
import { X, CheckCircle2, Loader2, Rocket, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { useLang } from '../context/LanguageContext';
import { useContent } from '../context/ContentContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PilotRequestModal = ({ products, preselect, onClose }) => {
  const { lang, t } = useLang();
  const offer = (useContent('products', null) || {}).founders_offer || {};
  const [form, setForm] = useState({
    product: preselect || (products[0] && products[0].id) || 'care',
    name: '', company: '', email: '', phone: ''
  });
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const selectedProduct = products.find((p) => p.id === form.product);
  const setField = (f, v) => setForm({ ...form, [f]: v });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.includes('@')) {
      setErrorMsg(t('pilot.required'));
      return;
    }
    setStatus('sending');
    setErrorMsg('');
    try {
      await axios.post(`${API}/pilot-request`, { ...form, language: lang });
      setStatus('success');
    } catch (error) {
      console.error('Pilot request failed:', error);
      setStatus('idle');
      setErrorMsg(t('pilot.error'));
    }
  };

  return (
    <div
      className="fixed inset-0 bg-[#1D1D1F]/40 backdrop-blur-md z-50 flex items-center justify-center p-6"
      onClick={onClose}
      data-testid="pilot-modal"
    >
      <div
        className="bg-white rounded-3xl max-w-md w-full p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${selectedProduct?.color || '#0071E3'}1A` }}>
            <Rocket className="w-6 h-6" style={{ color: selectedProduct?.color || '#0071E3' }} strokeWidth={1.8} />
          </div>
          <button onClick={onClose} className="text-[#86868B] hover:text-[#1D1D1F]" data-testid="pilot-modal-close">
            <X className="w-6 h-6" />
          </button>
        </div>

        {status === 'success' ? (
          <div className="text-center py-6" data-testid="pilot-success">
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-4" />
            <p className="text-[#1D1D1F] text-lg font-medium">{t('pilot.success')}</p>
          </div>
        ) : (
          <>
            <h3 className="text-2xl font-semibold text-[#1D1D1F] mb-2">{t('pilot.title')}</h3>
            <p className="text-sm text-[#86868B] mb-6">
              {t('pilot.subtitle').replace('{product}', selectedProduct?.name || '')}
            </p>

            {offer.enabled && (
              <div
                className="rounded-2xl px-4 py-3 mb-6 border"
                style={{
                  borderColor: `${selectedProduct?.color || '#0071E3'}40`,
                  backgroundColor: `${selectedProduct?.color || '#0071E3'}0D`,
                }}
                data-testid="pilot-offer-note"
              >
                <p className="text-sm font-semibold flex items-center gap-1.5" style={{ color: selectedProduct?.color || '#0071E3' }}>
                  <Sparkles className="w-4 h-4" />
                  {offer.badge}: {offer.price} {offer.price_period}
                </p>
                <p className="text-xs text-[#86868B] mt-1">
                  {offer.after_price} · {offer.limit_note}
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm text-[#1D1D1F] font-medium block mb-1.5">{t('pilot.product')}</label>
                <select
                  value={form.product}
                  onChange={(e) => setField('product', e.target.value)}
                  className="w-full bg-[#F5F5F7] border-0 rounded-xl h-12 px-4 text-[#1D1D1F] focus:outline-none focus:ring-1 focus:ring-[#0071E3]"
                  data-testid="pilot-product-select"
                >
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-[#1D1D1F] font-medium block mb-1.5">{t('pilot.name')}</label>
                <Input
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  className="bg-[#F5F5F7] border-0 rounded-xl h-12"
                  data-testid="pilot-name-input"
                />
              </div>
              <div>
                <label className="text-sm text-[#1D1D1F] font-medium block mb-1.5">
                  {t('pilot.company')} <span className="text-[#86868B] font-normal">{t('pilot.optional')}</span>
                </label>
                <Input
                  value={form.company}
                  onChange={(e) => setField('company', e.target.value)}
                  className="bg-[#F5F5F7] border-0 rounded-xl h-12"
                  data-testid="pilot-company-input"
                />
              </div>
              <div>
                <label className="text-sm text-[#1D1D1F] font-medium block mb-1.5">{t('pilot.email')}</label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                  className="bg-[#F5F5F7] border-0 rounded-xl h-12"
                  data-testid="pilot-email-input"
                />
              </div>
              <div>
                <label className="text-sm text-[#1D1D1F] font-medium block mb-1.5">
                  {t('pilot.phone')} <span className="text-[#86868B] font-normal">{t('pilot.optional')}</span>
                </label>
                <Input
                  value={form.phone}
                  onChange={(e) => setField('phone', e.target.value)}
                  className="bg-[#F5F5F7] border-0 rounded-xl h-12"
                  data-testid="pilot-phone-input"
                />
              </div>

              {errorMsg && (
                <p className="text-sm text-red-600" data-testid="pilot-error">{errorMsg}</p>
              )}

              <Button
                type="submit"
                disabled={status === 'sending'}
                className="w-full rounded-full h-12 text-white hover:opacity-90"
                style={{ backgroundColor: selectedProduct?.color || '#0071E3' }}
                data-testid="pilot-submit-button"
              >
                {status === 'sending' ? (
                  <><Loader2 className="mr-2 w-4 h-4 animate-spin" />{t('pilot.sending')}</>
                ) : (
                  t('pilot.submit')
                )}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default PilotRequestModal;
